import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import type { FieldDefinition, ReturnToReceive } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { Fab } from "@/components/shell/Fab";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { RecordDrawer } from "@/components/shell/RecordDrawer";
import { renderCell } from "@/components/table/cells";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { can, usePermissions } from "@/auth/permissions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { projectReturnRow } from "@/modules/orders/projections";
import { AddReturnDialog } from "@/modules/orders/returns/AddReturnDialog";
import { ReceiveParcel } from "@/modules/orders/returns/ReceiveParcel";
import { StatusPill } from "@/modules/orders/returns/ReceiptView";
import { StageBadge } from "@/modules/orders/screens/TrackingParts";
import type { SectionScreenProps } from "@/modules/types";
import { useOpenRecord } from "@/routes/useOpenRecord";

/**
 * Returns to receive (#529): every shipment whose delivery failed or that is
 * coming back, and that nobody has received, the one that has been returning
 * longest first. A section is a query, not a screen: the shared toolbar finds a
 * parcel by order number or tracking number, filters it and sorts it, and this
 * draws the rows.
 *
 * Opening a row opens the parcel over the list, as an order opens. The record
 * in the URL is the *shipment's* id, since a return is received per shipment.
 */

/**
 * How long it has been coming back. "about" when no scan dates the return and
 * the figure is an estimate (#558): never a dash for a parcel that has a stage.
 */
function days(row: ReturnToReceive): string {
  if (row.returningDays === null) return "—";
  return `${row.returningApproximate ? "about " : ""}${String(row.returningDays)} day${row.returningDays === 1 ? "" : "s"}`;
}

/** The small "added manually" mark on a return a person added (#594); hover says who, when and why. */
function ManualTag({ row }: { row: ReturnToReceive }) {
  if (!row.manual) return null;
  const when = new Date(row.manual.addedAt).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
  const summary = `Added by ${row.manual.addedByName ?? "someone"} on ${when}: ${row.manual.reason}`;
  return (
    <span
      title={summary}
      aria-label={summary}
      className="text-muted-foreground border-border inline-flex items-center rounded-sm border px-1 text-[0.6875rem] leading-4 font-medium"
    >
      added manually
    </span>
  );
}

function StageCell({ row }: { row: ReturnToReceive }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {row.stage ? (
        <StageBadge stage={row.stage} />
      ) : (
        <StatusPill tone="wait">Not delivered (Shopify)</StatusPill>
      )}
      <ManualTag row={row} />
    </span>
  );
}

function CourierSays({ row }: { row: ReturnToReceive }) {
  return row.rtoDelivered ? (
    <StatusPill tone="ok">RTO delivered</StatusPill>
  ) : (
    <span className="text-muted-foreground text-sm">Not yet</span>
  );
}

function ReturnMobileCard({ row }: { row: ReturnToReceive }) {
  return (
    <div className="min-w-0 space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate text-base font-semibold">
          {row.orderNumber}
        </span>
        <span className="text-muted-foreground min-w-0 truncate text-sm">
          {row.courier ?? "Courier not set"}
        </span>
      </div>
      <p className="text-muted-foreground text-sm tabular-nums wrap-anywhere">
        {row.trackingNumber ?? "No tracking number"}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <StageCell row={row} />
        {row.payment === "prepaid" ? (
          <Badge variant="outline" className="text-xs">
            Prepaid
          </Badge>
        ) : null}
      </div>
      <p className="text-muted-foreground text-sm">
        {row.returningDays === null ? "" : `Returning for ${days(row)} · `}
        {row.itemCount} item{row.itemCount === 1 ? "" : "s"}
      </p>
      {row.rtoDelivered ? null : (
        <p className="text-sm text-amber-700 dark:text-amber-300">
          The courier has not marked it RTO delivered yet.
        </p>
      )}
    </div>
  );
}

export function ReturnsList({
  fields,
  filter,
  search,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();
  const permissions = usePermissions();
  const [adding, setAdding] = useState(false);
  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];
  const { recordId, open } = useOpenRecord();

  const query = useQuery({
    queryKey: ["returns", "list", filter, search, sort, limit, cursor],
    queryFn: () =>
      data.returns.listToReceive({ filter, search, sort, limit, cursor }),
  });

  function renderOverride(field: FieldDefinition, row: ReturnToReceive) {
    switch (field.name) {
      case "stage":
        return <StageCell row={row} />;
      case "returningDays":
        return row.returningApproximate ? (
          <span title="An estimate: no scan dates the return, so this is counted from the newest update the shipment holds, or from the day it shipped.">
            {days(row)}
          </span>
        ) : (
          days(row)
        );
      case "rtoDelivered":
        return <CourierSays row={row} />;
      case "itemCount":
        return `${String(row.itemCount)} item${row.itemCount === 1 ? "" : "s"}`;
      case "courier":
        return row.courier ?? renderCell(field, null);
      default:
        return undefined;
    }
  }

  if (query.isPending) return <TableSkeleton />;
  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {can(permissions, "orders.returns.receive") ? (
        <>
          {/* A strip with one button on a desktop; a floating button on a phone (#617). */}
          <div className="hidden items-center justify-end border-b px-4 py-2 md:flex">
            <Button
              size="sm"
              onClick={() => {
                setAdding(true);
              }}
            >
              <Plus aria-hidden />
              Add a return
            </Button>
          </div>
          {recordId === null && !adding ? (
            <Fab
              label="Add a return"
              icon={<Plus aria-hidden />}
              onClick={() => {
                setAdding(true);
              }}
            />
          ) : null}
        </>
      ) : null}
      {adding ? (
        <AddReturnDialog
          open
          onOpenChange={setAdding}
          onAdded={() => {
            setHistory([undefined]);
          }}
        />
      ) : null}
      <DataTable
        fields={fields}
        rows={query.data.items}
        getRowId={(row) => row.id}
        project={projectReturnRow}
        sort={sort}
        onToggleSort={onToggleSort}
        onRowClick={(row) => {
          open(row.id);
        }}
        renderOverride={renderOverride}
        mobileRow={(row) => <ReturnMobileCard row={row} />}
        emptyState={<NoMatches onClear={onClearFilter} />}
        footer={
          <Pagination
            page={query.data}
            limit={limit}
            onLimitChange={(next) => {
              setLimit(next);
              setHistory([undefined]);
            }}
            history={history}
            onNavigate={setHistory}
          />
        }
      />

      {recordId !== null ? (
        <RecordDrawer
          // Not the order number: once the parcel is received it leaves the
          // list this reads from, and a title that changed under the person
          // who just pressed Confirm would read as the screen losing its place.
          // The order number is the first thing in the panel.
          title="Receive return"
          description="Check each item, then confirm"
          largeClose
        >
          <div className="flex h-full min-h-0 flex-col">
            <div className="bg-muted/30 min-h-0 flex-1 overflow-auto p-3 sm:p-4">
              <ReceiveParcel shipmentId={recordId} />
            </div>
          </div>
        </RecordDrawer>
      ) : null}
    </div>
  );
}
